import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Chip,
    Autocomplete
} from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import { COMPLEXITY_OPTIONS, COMPLEXITY_DETAILS } from '../common';

const GoalDialog = () => {
    const dispatch = useDispatch();

    // Redux state
    const isOpen = useSelector(selectors.isGoalDialogOpenSelector);
    const selectedGoal = useSelector(selectors.selectedGoalSelector);
    const loading = useSelector(selectors.loadingSelector);
    const departments = useSelector(selectors.uniqueDepartmentsSelector);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        complexity: '',
        departmentIds: []
    });

    const [errors, setErrors] = useState({});

    // Initialize form when dialog opens
    useEffect(() => {
        if (isOpen && selectedGoal) {
            // Handle both old single departmentId and new multiple departmentIds
            let departmentIds = [];
            if (selectedGoal.departmentIds && Array.isArray(selectedGoal.departmentIds)) {
                departmentIds = selectedGoal.departmentIds;
            } else if (selectedGoal.departmentId) {
                // Legacy: convert single departmentId to array
                departmentIds = [selectedGoal.departmentId];
            }
            
            setFormData({
                name: selectedGoal.name || '',
                description: selectedGoal.description || '',
                complexity: selectedGoal.complexity || '',
                departmentIds: departmentIds
            });
        } else if (isOpen && !selectedGoal) {
            setFormData({
                name: '',
                description: '',
                complexity: '',
                departmentIds: []
            });
        }
        setErrors({});
    }, [isOpen, selectedGoal]);

    // Handlers
    const handleClose = () => {
        dispatch(actions.closeGoalDialog());
        setFormData({
            name: '',
            description: '',
            complexity: '',
            departmentIds: []
        });
        setErrors({});
    };

    const handleChange = (field) => (event) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.value
        }));
        // Clear error for this field
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name || formData.name.trim() === '') {
            newErrors.name = 'Name is required';
        } else if (formData.name.trim().length < 3) {
            newErrors.name = 'Name must be at least 3 characters long';
        } else if (formData.name.trim().length > 255) {
            newErrors.name = 'Name must not exceed 255 characters';
        }

        if (!formData.description || formData.description.trim() === '') {
            newErrors.description = 'Description is required';
        } else if (formData.description.trim().length < 10) {
            newErrors.description = 'Description must be at least 10 characters long';
        } else if (formData.description.trim().length > 1000) {
            newErrors.description = 'Description must not exceed 1000 characters';
        }

        if (!formData.complexity || formData.complexity === '') {
            newErrors.complexity = 'Complexity is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) {
            return;
        }

        const payload = {
            name: formData.name.trim(),
            description: formData.description.trim(),
            complexity: formData.complexity
        };

        // Add departmentIds array if departments are selected (convert to strings)
        if (formData.departmentIds && formData.departmentIds.length > 0) {
            payload.departmentIds = formData.departmentIds.map(id => String(id));
        }

        if (selectedGoal) {
            // Update existing goal
            dispatch(actions.updateGoal(selectedGoal.id, payload));
        } else {
            // Create new goal
            dispatch(actions.createGoal(payload));
        }
    };

    const isEditMode = !!selectedGoal;

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '400px' }
            }}
        >
            <DialogTitle>
                {isEditMode ? 'Edit Goal' : 'Create New Goal'}
            </DialogTitle>

            <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <Box sx={{ pt: 2 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                fullWidth
                                required
                                value={formData.name}
                                onChange={handleChange('name')}
                                error={!!errors.name}
                                helperText={errors.name}
                                placeholder="e.g., Task Completion Efficiency"
                                inputProps={{ maxLength: 255 }}
                                size="small"
                            />
                        </Grid>

                        {/* Description */}
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                fullWidth
                                required
                                multiline
                                rows={4}
                                value={formData.description}
                                onChange={handleChange('description')}
                                error={!!errors.description}
                                helperText={errors.description || `${formData.description.length}/1000 characters`}
                                placeholder="Provide a detailed description of the goal..."
                                inputProps={{ maxLength: 1000 }}
                                size="small"
                            />
                        </Grid>

                        {/* Complexity */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required error={!!errors.complexity} size="small">
                                <InputLabel>Complexity</InputLabel>
                                <Select
                                    value={formData.complexity}
                                    label="Complexity"
                                    onChange={handleChange('complexity')}
                                >
                                    {COMPLEXITY_OPTIONS.filter(opt => opt.value !== '').map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.complexity && (
                                    <FormHelperText>{errors.complexity}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Department - Searchable Multi-select */}
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                multiple
                                options={departments}
                                getOptionLabel={(option) => option.name || ''}
                                value={departments.filter(dept => formData.departmentIds.includes(dept.id))}
                                onChange={(event, newValue) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        departmentIds: newValue.map(dept => dept.id)
                                    }));
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Departments (Optional)"
                                        placeholder="Search departments..."
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            label={option.name}
                                            {...getTagProps({ index })}
                                            size="small"
                                        />
                                    ))
                                }
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    startIcon={<CloseIcon />}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                >
                    {isEditMode ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GoalDialog;
